import { useCallback, useMemo } from "react";
import styled from "styled-components";
import { ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { REQUEST_ID } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { useQueryClient } from "@tanstack/react-query";
import {
  useExpected,
  useExpectedCheck,
  useModalExpected,
} from "~/client/hooks";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    width: 100%;
    padding: calc(${(props) => props.theme.sizes.edge} * 1)
      ${(props) => props.theme.sizes.modalEdge};
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Explainer = styled.div`
  margin-right: auto;

  & > p {
    ${(props) => props.theme.styles.textParagraphMini}
    & {
      color: ${(props) => props.theme.colors.gray400};
    }
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    column-gap: ${(props) => props.theme.sizes.edge};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    padding: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
  }

  ${(props) => props.theme.medias.maxSM} {
    ${Explainer} {
      display: none;
    }
    ${Actions} {
      width: 100%;
      column-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
      & > * {
        flex: 1;
      }
    }
  }
`;

export default function Footer() {
  const { list, reset } = useExpected();
  const client = useQueryClient();
  const { doClose } = useModalExpected();
  const { isLoading } = useExpectedCheck();
  const size = useMemo(() => list.length, [list]);
  const { t } = useT();

  const doClear = useCallback(() => {
    reset();
    doClose();
  }, [reset, doClose]);

  const doRefresh = useCallback(() => {
    if (!isLoading) {
      void client.resetQueries({ queryKey: REQUEST_ID.expected });
    }
  }, [client, isLoading]);

  return (
    <Wrapper>
      <Explainer>
        {size ? <p>{t("modalCards.expected.footer", { size })}</p> : false}
      </Explainer>
      <Actions>
        <Button
          accent={"iconic400"}
          appearance={"outline"}
          isDisabled={size === 0}
          isLoading={isLoading}
          isMini
          onClick={doClear}
          right={TrashIcon}
          title={t("structs.clearList")}
        />
        <Button
          accent={"iconic400"}
          appearance={"outline"}
          isDisabled={size === 0}
          isLoading={isLoading}
          isMini
          onClick={doRefresh}
          right={ArrowPathIcon}
          title={t("structs.checkAgain")}
        />
      </Actions>
    </Wrapper>
  );
}
